// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Badge, Box, Button, Card, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconBuilding, IconCheck, IconUser } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './MembershipSelector.module.css';

/**
 * Represents a project membership from Medplum login response
 * This mirrors the structure returned by medplum.startLogin()
 */
export interface ProjectMembership {
  id: string;
  project: {
    reference: string;
    display?: string;
  };
  profile: {
    reference: string;
    display?: string;
  };
  admin?: boolean;
}

interface MembershipSelectorProps {
  /** Array of available project memberships from login response */
  memberships: ProjectMembership[];
  /** Callback when user selects a membership */
  onSelect: (membership: ProjectMembership) => void;
  /** Whether a selection is being processed */
  loading?: boolean;
  /** ID of the membership currently being selected */
  selectingId?: string;
  /** ID of a membership that failed to load (for error display) */
  failedMembershipId?: string;
  /** Error message for the failed membership */
  errorMessage?: string;
}

/**
 * MembershipSelector Component
 *
 * Displays available Medplum project memberships for the user to choose from.
 * This is shown during login when a user has access to multiple projects.
 *
 * In Medplum's multi-tenant architecture:
 * - Projects provide hard HIPAA boundaries between unrelated healthcare organizations
 * - Users can belong to multiple projects (e.g., a consultant working with multiple clinics)
 * - Each project membership has its own profile and access context
 */
export function MembershipSelector({
  memberships,
  onSelect,
  loading = false,
  selectingId,
  failedMembershipId,
  errorMessage,
}: MembershipSelectorProps): JSX.Element {
  const { t } = useTranslation();

  // Extract project ID from reference like "Project/abc123"
  const getProjectId = (reference: string): string => {
    return reference.split('/')[1] || reference;
  };

  // Extract profile type from reference like "Practitioner/xyz789"
  const getProfileType = (reference: string): string => {
    return reference.split('/')[0] || 'User';
  };

  return (
    <Stack gap="lg">
      <Box ta="center">
        <Title order={3} mb="xs">
          {t('auth.selectProject', 'Select a Project')}
        </Title>
        <Text size="sm" c="dimmed">
          {t('auth.selectProjectDescription', 'You have access to multiple healthcare projects. Please select one to continue.')}
        </Text>
      </Box>

      {/* Show error alert if a membership failed */}
      {failedMembershipId && errorMessage && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('auth.projectUnavailable', 'Project Unavailable')}
          color="orange"
          variant="light"
        >
          {t('auth.projectUnavailableDescription', 'This project could not be accessed. Please try another project or contact your administrator.')}
          <Text size="xs" c="dimmed" mt="xs">
            {errorMessage}
          </Text>
        </Alert>
      )}

      <Stack gap="sm">
        {memberships.map((membership) => {
          const projectId = getProjectId(membership.project.reference);
          const profileType = getProfileType(membership.profile.reference);
          const isSelecting = selectingId === membership.id;
          const hasFailed = failedMembershipId === membership.id;
          const isDisabled = loading;

          return (
            <Card
              key={membership.id}
              className={classes.membershipCard}
              padding="md"
              radius="md"
              withBorder
              onClick={() => !isDisabled && onSelect(membership)}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                borderColor: hasFailed ? 'var(--mantine-color-orange-4)' : undefined,
                backgroundColor: hasFailed ? 'var(--mantine-color-orange-0)' : undefined,
              }}
              data-selecting={isSelecting}
              data-failed={hasFailed}
            >
              <Group justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <Box className={classes.iconWrapper} style={hasFailed ? { backgroundColor: 'var(--mantine-color-orange-1)' } : undefined}>
                    {hasFailed ? <IconAlertCircle size={20} color="var(--mantine-color-orange-6)" /> : <IconBuilding size={20} />}
                  </Box>
                  <Box>
                    <Text fw={600} size="sm" lineClamp={1}>
                      {membership.project.display || `Project ${projectId.slice(0, 8)}...`}
                    </Text>
                    <Group gap="xs">
                      <IconUser size={12} color="gray" />
                      <Text size="xs" c="dimmed">
                        {membership.profile.display || profileType}
                      </Text>
                    </Group>
                    {hasFailed && (
                      <Text size="xs" c="orange" mt={2}>
                        {t('auth.tapToRetry', 'Tap to retry')}
                      </Text>
                    )}
                  </Box>
                </Group>

                <Group gap="xs" wrap="nowrap">
                  {membership.admin && (
                    <Badge size="xs" variant="light" color="violet">
                      {t('auth.admin', 'Admin')}
                    </Badge>
                  )}
                  {hasFailed && (
                    <Badge size="xs" variant="light" color="orange">
                      {t('auth.failed', 'Failed')}
                    </Badge>
                  )}
                  {isSelecting ? (
                    <Loader size="xs" />
                  ) : !hasFailed && (
                    <IconCheck size={16} className={classes.checkIcon} />
                  )}
                </Group>
              </Group>
            </Card>
          );
        })}
      </Stack>

      {loading && (
        <Text size="sm" c="dimmed" ta="center">
          {t('auth.selectingProject', 'Connecting to project...')}
        </Text>
      )}

      <Button
        variant="subtle"
        size="sm"
        onClick={() => window.location.reload()}
        disabled={loading}
      >
        {t('auth.signInDifferentAccount', 'Sign in with a different account')}
      </Button>
    </Stack>
  );
}
